import { throwUserFacingHttpError } from "lib/utils/userFacingApiError";
import {
    appendMapFilterGeometryToFormData,
    type MapFilterGeometry,
} from "../protocols/mapFilterYaml";
import {
    parseMapEditorLiveGridSnapshot,
    type MapEditorLiveGridSnapshot,
} from "../navigation/mapEditorGrid";
import { isHttpSecure } from "lib/utils/utils";
import { joinUrl } from "lib/utils/connection";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { getAuthToken } from "lib/utils/auth";
import { authedFetch } from "lib/utils/authedFetch";

export type { MapFilterGeometry };
export { mapFilterGeometryFromMetadata } from "../protocols/mapFilterYaml";

/** Base URL for the STCM map list HTTP service (no trailing slash). */

/** Edited variant entries from mission_storage (GET /stcm-files → edited_bmps). */
export type EditedBmpListItem = {
    path: string;
    saved_at?: string;
};

export type StcmFilesResponse = {
    directory: string;
    exists: boolean;
    count?: number;
    files: string[];
    /** Edited raster-only maps (not shown as a .stcm row). May be strings (legacy) or objects. */
    edited_bmps?: (string | EditedBmpListItem)[];
};

/** Companion `.bmp` path for a relative `.stcm` path (same directory / basename). */
export function stcmRelToCompanionBmpRel(stcmRelativePath: string): string {
    return stcmRelativePath.replace(/\.stcm$/i, ".bmp");
}

/** POST edited variant image (`.png` preferred, `.bmp` supported) to map_provider_server. */
export async function postSaveVariantImage(
    baseUrl: string,
    relBmpPath: string,
    imageBlob: Blob,
    options?: { sourceStcmRelPath?: string; mapFilterGeometry?: MapFilterGeometry },
): Promise<{ ok: boolean; written: string }> {
    const form = new FormData();
    form.append("rel_path", relBmpPath.replace(/\\/g, "/").trim());
    form.append("file", imageBlob, "composite.png");
    const src = options?.sourceStcmRelPath?.trim();
    if (src) {
        form.append("source_stcm_rel", src);
    }
    appendMapFilterGeometryToFormData(form, options?.mapFilterGeometry);
    const url = joinUrl(baseUrl, "/save-image");
    const res = await authedFetch(url, { method: "POST", body: form });
    if (!res.ok) {
        await throwUserFacingHttpError(res, "Couldn't complete the map request. Try again.");
    }
    return res.json() as Promise<{ ok: boolean; written: string }>;
}

/** Backward-compatible alias; prefer `postSaveVariantImage`. */
export const postSaveBmp = postSaveVariantImage;

/** Basename of a relative `.stcm` path, without the `.stcm` suffix (for UI labels). */
export function stcmDisplayName(relativePath: string): string {
    const base = relativePath.split("/").pop() ?? relativePath;
    return base.replace(/\.stcm$/i, "");
}

/** Label for a storage row (.stcm or standalone .bmp). */
export function storageMapRowLabel(relativePath: string): string {
    const base = relativePath.split("/").pop() ?? relativePath;
    if (base.toLowerCase().endsWith(".bmp") || base.toLowerCase().endsWith(".png")) {
        return base.replace(/\.(bmp|png)$/i, "");
    }
    return stcmDisplayName(relativePath);
}

/** Re-open Map Manager on the control page with the same selection after leaving the map editor. */
export type MapManagerRestorePayload = {
    focusedStcm: string;
    previewSelection: string;
};

/** Parent directory with trailing slash, and file stem without `.stcm` / `.bmp`. */
export function storageRelPathDirAndStem(relPath: string): { dir: string; stem: string } {
    const norm = relPath.replace(/\\/g, "/").trim();
    const i = norm.lastIndexOf("/");
    const dir = i >= 0 ? norm.slice(0, i + 1) : "";
    const base = i >= 0 ? norm.slice(i + 1) : norm;
    const stem = base.replace(/\.(stcm|bmp|png)$/i, "");
    return { dir, stem };
}

/** Map editor suffix only: trim, spaces → `_`, strip path chars. */
export function sanitizeMapEditorSaveSuffix(raw: string): string {
    return raw
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[\\/]+/g, "")
        .replace(/^\.+/, "");
}

export function parseEditedBmpsFromResponse(raw: StcmFilesResponse["edited_bmps"]): EditedBmpListItem[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    const out: EditedBmpListItem[] = [];
    for (const x of raw) {
        if (typeof x === "string" && x.trim()) {
            out.push({ path: x.trim().replace(/\\/g, "/") });
        } else if (x && typeof x === "object" && typeof (x as EditedBmpListItem).path === "string") {
            const o = x as EditedBmpListItem;
            out.push({
                path: o.path.trim().replace(/\\/g, "/"),
                ...(typeof o.saved_at === "string" ? { saved_at: o.saved_at } : {}),
            });
        }
    }
    return out;
}

/**
 * Browser `<img>` / `Image` cannot set Authorization. arc_gateway `forward_auth`
 * accepts the ARC JWT as `?token=` (same as WebSocket routes).
 */
function withGatewayAuthToken(url: URL): string {
    const token = getAuthToken();
    if (token) {
        url.searchParams.set("token", token);
    }
    return url.toString();
}

/** URL to fetch the companion .bmp preview for a relative .stcm path (GET /preview-bmp?rel_path=…). */
export function getStcmPreviewBmpUrl(baseUrl: string, stcmRelativePath: string): string {
    const u = new URL(joinUrl(baseUrl, "/preview-bmp"));
    u.searchParams.set("rel_path", stcmRelativePath.replace(/\\/g, "/").trim());
    return withGatewayAuthToken(u);
}

/** Preview URL for a storage row: companion bmp from .stcm, or direct .bmp for edited-only maps. */
export function getStorageMapPreviewBmpUrl(baseUrl: string, storageRelativePath: string): string {
    const p = storageRelativePath.trim();
    if (p.toLowerCase().endsWith(".bmp") || p.toLowerCase().endsWith(".png")) {
        const u = new URL(joinUrl(baseUrl, "/preview-bmp-direct"));
        u.searchParams.set("rel_path", p.replace(/\\/g, "/").trim());
        return withGatewayAuthToken(u);
    }
    return getStcmPreviewBmpUrl(baseUrl, p);
}

const MAP_EDITOR_STORAGE_STCM_KEY = "webapp.mapEditor.storageStcmEdit";

export type StorageStcmEditPayload = {
    baseUrl: string;
    relPath: string;
    /** Parent `.stcm` when `relPath` is an edited variant; used to size the editor grid from the base map. */
    parentStcmRelPath?: string;
    /** Live `/map_metadata` grid from Control when the editor was opened (fleet route / reload). */
    liveMapGrid?: MapEditorLiveGridSnapshot;
};

function parseStoredStorageStcmEdit(raw: string | null): StorageStcmEditPayload | null {
    if (!raw) return null;
    try {
        const o = JSON.parse(raw) as unknown;
        if (
            o &&
            typeof o === "object" &&
            typeof (o as StorageStcmEditPayload).baseUrl === "string" &&
            typeof (o as StorageStcmEditPayload).relPath === "string"
        ) {
            const payload = o as StorageStcmEditPayload;
            const liveMapGrid = parseMapEditorLiveGridSnapshot(
                (payload as { liveMapGrid?: unknown }).liveMapGrid,
            );
            return {
                baseUrl: payload.baseUrl,
                relPath: payload.relPath,
                ...(typeof payload.parentStcmRelPath === "string" && payload.parentStcmRelPath.trim()
                    ? { parentStcmRelPath: payload.parentStcmRelPath.trim().replace(/\\/g, "/") }
                    : {}),
                ...(liveMapGrid ? { liveMapGrid } : {}),
            };
        }
    } catch {
        /* ignore */
    }
    return null;
}

/** Prefer React Router location.state; sessionStorage survives a full page reload. */
export function getStorageStcmEditFromLocation(state: unknown): StorageStcmEditPayload | null {
    const fromState = (state as { storageStcmEdit?: Partial<StorageStcmEditPayload> } | null | undefined)
        ?.storageStcmEdit;
    if (fromState?.baseUrl && fromState?.relPath) {
        const liveMapGrid = parseMapEditorLiveGridSnapshot(fromState.liveMapGrid);
        return {
            baseUrl: fromState.baseUrl,
            relPath: fromState.relPath,
            ...(typeof fromState.parentStcmRelPath === "string" &&
            fromState.parentStcmRelPath.trim()
                ? { parentStcmRelPath: fromState.parentStcmRelPath.trim().replace(/\\/g, "/") }
                : {}),
            ...(liveMapGrid ? { liveMapGrid } : {}),
        };
    }
    return parseStoredStorageStcmEdit(sessionStorage.getItem(MAP_EDITOR_STORAGE_STCM_KEY));
}

/** Persist when opening the editor so reload does not drop context. */
export function persistStorageStcmEditFromLocation(state: unknown): void {
    const fromState = (state as { storageStcmEdit?: Partial<StorageStcmEditPayload> } | null | undefined)
        ?.storageStcmEdit;
    if (fromState?.baseUrl && fromState?.relPath) {
        const payload: StorageStcmEditPayload = {
            baseUrl: fromState.baseUrl,
            relPath: fromState.relPath,
        };
        if (
            typeof fromState.parentStcmRelPath === "string" &&
            fromState.parentStcmRelPath.trim()
        ) {
            payload.parentStcmRelPath = fromState.parentStcmRelPath
                .trim()
                .replace(/\\/g, "/");
        }
        const liveMapGrid = parseMapEditorLiveGridSnapshot(fromState.liveMapGrid);
        if (liveMapGrid) {
            payload.liveMapGrid = liveMapGrid;
        }
        sessionStorage.setItem(MAP_EDITOR_STORAGE_STCM_KEY, JSON.stringify(payload));
    }
}

export function clearPersistedStorageStcmEdit(): void {
    sessionStorage.removeItem(MAP_EDITOR_STORAGE_STCM_KEY);
}

/** One edited BMP variant linked to a `.stcm` (sidecar JSON). */
export type StcmEditedBmpVariantMeta = {
    bmp_rel_path: string;
    remarks?: string;
    saved_at?: string;
};

/** Sidecar JSON next to each `.stcm` (written by POST /stcm-metadata). */
export type StcmMapFileMetadata = {
    saved_at: string | null;
    remarks: string;
    edited_bmp_variants?: StcmEditedBmpVariantMeta[];
    source_stcm_rel_path?: string;
};

export const STCM_REMARKS_MAX_LENGTH = 10000;

/**
 * Write `{basename}_filter.yaml` beside the map (POST /map-filter-yaml).
 * Robot map-list service must implement this route (same base URL as /stcm-metadata).
 */
export async function postMapFilterYaml(
    baseUrl: string,
    relativePath: string,
    yamlContent: string,
): Promise<void> {
    const url = joinUrl(baseUrl, "/map-filter-yaml");
    const res = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            rel_path: relativePath.replace(/\\/g, "/").trim(),
            content: yamlContent,
        }),
    });
    if (!res.ok) {
        await throwUserFacingHttpError(res, "Couldn't complete the map request. Try again.");
    }
}

/** Write `{basename}.json` beside the .stcm (POST /stcm-metadata). */
export async function postStcmMetadata(
    baseUrl: string,
    stcmRelativePath: string,
    remarks: string,
    options?: {
        variantBmpRelPath?: string;
        /** If set, server writes `{stem}_filter.yaml` (same request as metadata; works without POST /map-filter-yaml). */
        filterYamlContent?: string;
    },
): Promise<void> {
    const v = options?.variantBmpRelPath?.replace(/\\/g, "/").trim();
    const fy = options?.filterYamlContent;
    const url = joinUrl(baseUrl, "/stcm-metadata");
    const res = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            rel_path: stcmRelativePath,
            remarks: remarks.slice(0, STCM_REMARKS_MAX_LENGTH),
            ...(v ? { variant_bmp_rel_path: v } : {}),
            ...(fy !== undefined && fy !== ""
                ? { filter_yaml_content: fy }
                : {}),
        }),
    });
    if (!res.ok) {
        await throwUserFacingHttpError(res, "Couldn't complete the map request. Try again.");
    }
}

const emptyStcmMetadata = (relStcm: string): StcmMapFileMetadata => ({
    remarks: "",
    saved_at: null,
    edited_bmp_variants: [],
    source_stcm_rel_path: relStcm.replace(/\\/g, "/").trim(),
});

/** Fetch sidecar metadata (GET /stcm-metadata). New servers return 200 with defaults when JSON is absent; older servers may 404. */
export async function fetchStcmMetadata(
    baseUrl: string,
    stcmRelativePath: string,
): Promise<StcmMapFileMetadata> {
    const rel = stcmRelativePath.replace(/\\/g, "/").trim();
    const fetchStcmMetaUrl = new URL(joinUrl(baseUrl, "/stcm-metadata"));
    fetchStcmMetaUrl.searchParams.set("rel_path", rel);
    const res = await authedFetch(fetchStcmMetaUrl.toString());
    if (res.status === 404) {
        return emptyStcmMetadata(rel);
    }
    if (!res.ok) {
        await throwUserFacingHttpError(res, "Couldn't complete the map request. Try again.");
    }
    return res.json() as Promise<StcmMapFileMetadata>;
}

/** Delete a stored .stcm and its companion .bmp on the map list server (DELETE /stcm-file?rel_path=…). */
export async function deleteEditedBmpStored(baseUrl: string, bmpRelativePath: string): Promise<void> {
    const u = new URL(joinUrl(baseUrl, "/edited-bmp"));
    u.searchParams.set("rel_path", bmpRelativePath.replace(/\\/g, "/").trim());
    const url = u.toString();
    const res = await authedFetch(url, { method: "DELETE" });
    if (!res.ok) {
        await throwUserFacingHttpError(res, "Couldn't complete the map request. Try again.");
    }
}

export async function deleteStcmStoredMap(
    baseUrl: string,
    stcmRelativePath: string,
): Promise<void> {
    const u = new URL(joinUrl(baseUrl, "/stcm-file"));
    u.searchParams.set("rel_path", stcmRelativePath.replace(/\\/g, "/").trim());
    const url = u.toString();
    const res = await authedFetch(url, { method: "DELETE" });
    if (!res.ok) {
        await throwUserFacingHttpError(res, "Couldn't complete the map request. Try again.");
    }
}

/** Port must match the STCM list server (e.g. config.yaml on the robot). */
const DEFAULT_STCM_LIST_PORT = "12393";

/**
 * Base URL for the STCM HTTP service on the robot.
 * @param arcHost When set (e.g. `selectedARC.host` in fleet mode), used before single-ARC env hostname.
 */
export function getStcmMapListBaseUrl(arcHost?: string | null, isSecure: boolean = isHttpSecure(), arcId?: string | null): string | null {
    if (process.env.REACT_APP_STCM_MAP_LIST_DISABLED === "true") {
        return null;
    }
    const explicit = process.env.REACT_APP_STCM_MAP_LIST_BASE_URL?.trim();
    if (explicit) {
        return explicit.replace(/\/$/, "");
    }
    const port =
        process.env.REACT_APP_STCM_MAP_LIST_PORT?.trim() || DEFAULT_STCM_LIST_PORT;
    const host =
        process.env.REACT_APP_STCM_MAP_LIST_HOST?.trim() ||
        arcHost?.trim() ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) {
        return null;
    }
    const protocol = isSecure ? "https" : "http";
    // Direct mode uses arc_gateway; fleet mode uses the proxy's path-routed
    // origin. A fleet ARC without a proxy host is unavailable.
    const gatewayBase = gatewayServiceBaseUrl("map", arcId, arcHost);
    if (gatewayBase) return gatewayBase;
    if (arcId) return null;

    return `${protocol}://${host}:${port}`;
}

/** List `.stcm` paths from GET /stcm-files (same as Map Manager storage list). */
export async function fetchStcmFilesList(baseUrl: string): Promise<StcmFilesResponse> {
    const url = joinUrl(baseUrl, "/stcm-files");
    const res = await authedFetch(url, { cache: "no-store" });
    if (!res.ok) {
        throw new Error("Couldn't complete the map request. Try again.");
    }
    return res.json() as Promise<StcmFilesResponse>;
}
